// Used when Best Offer is enabled but neither an explicit price nor a category
// AutoAcceptThreshold is available. Keep in lockstep with the backend constant
// BestOfferPricing.DefaultAutoAcceptThresholdPercent (GPMetals.Core/Services/BestOfferPricing.cs).
export const DEFAULT_AUTO_ACCEPT_THRESHOLD_PERCENT = 10;

/**
 * Mirrors backend BestOfferPricing.ResolveAutoAcceptPrice.
 * Precedence: explicit price > category threshold % off BIN > global default % off BIN.
 * Returns null when it cannot be computed (no valid Buy It Now price).
 */
export function resolveEffectiveAutoAcceptPrice(
  buyItNowPrice: number | null | undefined,
  explicitPrice: number | null | undefined,
  categoryThreshold: number | null | undefined,
): number | null {
  if (explicitPrice && explicitPrice > 0) return explicitPrice;
  if (!buyItNowPrice || buyItNowPrice <= 0) return null;

  const threshold =
    categoryThreshold && categoryThreshold > 0
      ? categoryThreshold
      : DEFAULT_AUTO_ACCEPT_THRESHOLD_PERCENT;

  return Math.round(buyItNowPrice * (1 - threshold / 100) * 100) / 100;
}

/**
 * Mirrors backend BestOfferPricing.CarryAutoAcceptPrice: when the Buy It Now price changes and the save
 * leaves the auto-accept alone, the API moves it to the same % below the new price it sat at before.
 * Returns that price and %, or null when there's no % to carry (no previous auto-accept, or one not
 * below the old price) - the API then falls back to the category threshold.
 */
export function carryAutoAcceptPrice(
  previousBuyItNow: number | null | undefined,
  previousAutoAccept: number | null | undefined,
  newBuyItNow: number | null | undefined,
): { price: number; percent: number } | null {
  if (!newBuyItNow || newBuyItNow <= 0) return null;
  if (!previousBuyItNow || previousBuyItNow <= 0) return null;
  if (!previousAutoAccept || previousAutoAccept <= 0 || previousAutoAccept >= previousBuyItNow) return null;

  // To a tenth of a percent - both stored figures are already rounded to the penny, so the raw ratio
  // would creep a penny or so every time the price moved.
  const percent = Math.round((1 - previousAutoAccept / previousBuyItNow) * 1000) / 10;

  return { price: Math.round(newBuyItNow * (1 - percent / 100) * 100) / 100, percent };
}

/**
 * The Best Offer auto-accept to stage alongside a new Buy It Now price on the optimisation drawer:
 * the category threshold, else the global default, off the new price. Deliberately not the live
 * pair's % - a repriced listing is meant to start again from the yard's standard discount, not
 * inherit a 1% gap someone once typed. Null when there is no new price to work from.
 */
export function defaultAutoAcceptFor(
  newBuyItNow: number | null | undefined,
  categoryThreshold: number | null | undefined,
): number | null {
  if (!newBuyItNow || newBuyItNow <= 0) return null;

  return resolveEffectiveAutoAcceptPrice(newBuyItNow, null, categoryThreshold);
}
